import { reactive, ref } from 'vue';

const MIN_ZOOM = 0.25;
const MAX_ZOOM = 2;
const ZOOM_STEP = 0.1;
/** Wheel pan feels harsh at 1:1 — damp input and ease toward the target. */
const WHEEL_SENSITIVITY = 0.42;
const WHEEL_EASING = 0.22;

/**
 * Pixel-space pan/zoom for the portal editor infinite canvas.
 * Transform is applied imperatively to avoid Vue re-renders (and iframe
 * repaints triggered by reactive style bindings) on every pointer/wheel tick.
 */
export function useEditorCanvasViewport() {
  const pan = reactive({ x: 40, y: 40 });
  const zoom = ref(1);
  const zoomPercent = ref(100);
  const isPanning = ref(false);
  const spaceHeld = ref(false);
  const panStart = reactive({ x: 0, y: 0 });
  const panOrigin = reactive({ x: 0, y: 0 });

  let worldEl = null;
  let viewportEl = null;
  let zoomLabelEl = null;
  let rafId = 0;
  let wheelRafId = 0;
  let moveRafId = 0;
  let pendingMoveX = 0;
  let pendingMoveY = 0;
  let hasPendingMove = false;
  let pendingPanDx = 0;
  let pendingPanDy = 0;
  let hasPendingPan = false;
  let wheelTargetX = 40;
  let wheelTargetY = 40;
  let zoomUiRafId = 0;

  function clampZoom(level) {
    return Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, level));
  }

  function flushTransform() {
    if (!worldEl) return;
    worldEl.style.transform = `translate3d(${pan.x}px, ${pan.y}px, 0) scale(${zoom.value})`;
  }

  function flushZoomLabel() {
    const pct = Math.round(zoom.value * 100);
    if (zoomPercent.value !== pct) zoomPercent.value = pct;
    if (zoomLabelEl) zoomLabelEl.textContent = `${pct}%`;
  }

  function scheduleZoomLabel() {
    if (zoomUiRafId) return;
    zoomUiRafId = requestAnimationFrame(() => {
      zoomUiRafId = 0;
      flushZoomLabel();
    });
  }

  function syncCursor() {
    if (!viewportEl) return;
    viewportEl.style.cursor =
      isPanning.value || spaceHeld.value ? 'grabbing' : 'grab';
  }

  function stopWheelEasing() {
    if (wheelRafId) {
      cancelAnimationFrame(wheelRafId);
      wheelRafId = 0;
    }
    wheelTargetX = pan.x;
    wheelTargetY = pan.y;
  }

  function bindWorld(el) {
    worldEl = el || null;
    if (worldEl) {
      worldEl.style.transformOrigin = '0 0';
      worldEl.style.willChange = 'transform';
      flushTransform();
    }
  }

  function bindViewport(el) {
    viewportEl = el || null;
    syncCursor();
  }

  function bindZoomLabel(el) {
    zoomLabelEl = el || null;
    flushZoomLabel();
  }

  function setZoomValue(level) {
    zoom.value = clampZoom(level);
    scheduleZoomLabel();
  }

  function zoomTo(level) {
    setZoomValue(level);
    flushTransform();
    flushZoomLabel();
  }

  function zoomIn() {
    setZoomValue(zoom.value * (1 + ZOOM_STEP));
    flushTransform();
  }

  function zoomOut() {
    setZoomValue(zoom.value * (1 - ZOOM_STEP));
    flushTransform();
  }

  function resetView() {
    stopWheelEasing();
    pan.x = 40;
    pan.y = 40;
    wheelTargetX = pan.x;
    wheelTargetY = pan.y;
    setZoomValue(1);
    flushTransform();
    flushZoomLabel();
  }

  /** Zoom toward a point in the viewport (coords relative to container). */
  function zoomAt(level, localX, localY) {
    const next = clampZoom(level);
    if (next === zoom.value) return;
    stopWheelEasing();
    const worldX = (localX - pan.x) / zoom.value;
    const worldY = (localY - pan.y) / zoom.value;
    setZoomValue(next);
    pan.x = localX - worldX * next;
    pan.y = localY - worldY * next;
    wheelTargetX = pan.x;
    wheelTargetY = pan.y;
    flushTransform();
  }

  function panBy(dx, dy) {
    pan.x += dx;
    pan.y += dy;
    wheelTargetX = pan.x;
    wheelTargetY = pan.y;
    flushTransform();
  }

  /** Coalesce high-frequency pan deltas onto the next animation frame. */
  function panByRaf(dx, dy) {
    pendingPanDx += dx;
    pendingPanDy += dy;
    hasPendingPan = true;
    if (rafId) return;
    rafId = requestAnimationFrame(() => {
      rafId = 0;
      if (!hasPendingPan) return;
      const dxPending = pendingPanDx;
      const dyPending = pendingPanDy;
      pendingPanDx = 0;
      pendingPanDy = 0;
      hasPendingPan = false;
      panBy(dxPending, dyPending);
    });
  }

  function normalizeWheelDelta(deltaX, deltaY, deltaMode) {
    let dx = deltaX;
    let dy = deltaY;
    // 0 = pixels, 1 = lines, 2 = pages
    if (deltaMode === 1) {
      dx *= 16;
      dy *= 16;
    } else if (deltaMode === 2) {
      dx *= 600;
      dy *= 600;
    }
    return { dx: dx * WHEEL_SENSITIVITY, dy: dy * WHEEL_SENSITIVITY };
  }

  function tickWheelEase() {
    wheelRafId = 0;
    const dx = wheelTargetX - pan.x;
    const dy = wheelTargetY - pan.y;
    if (Math.abs(dx) < 0.15 && Math.abs(dy) < 0.15) {
      pan.x = wheelTargetX;
      pan.y = wheelTargetY;
      flushTransform();
      return;
    }
    pan.x += dx * WHEEL_EASING;
    pan.y += dy * WHEEL_EASING;
    flushTransform();
    wheelRafId = requestAnimationFrame(tickWheelEase);
  }

  /** Softened wheel/trackpad pan with damped input + ease-out. */
  function wheelPanBy(deltaX, deltaY, deltaMode = 0) {
    if (isPanning.value) return;
    const { dx, dy } = normalizeWheelDelta(deltaX, deltaY, deltaMode);
    if (!dx && !dy) return;
    if (!wheelRafId) {
      wheelTargetX = pan.x;
      wheelTargetY = pan.y;
    }
    wheelTargetX += dx;
    wheelTargetY += dy;
    if (!wheelRafId) {
      wheelRafId = requestAnimationFrame(tickWheelEase);
    }
  }

  function startPan(clientX, clientY) {
    stopWheelEasing();
    isPanning.value = true;
    panStart.x = clientX;
    panStart.y = clientY;
    panOrigin.x = pan.x;
    panOrigin.y = pan.y;
    worldEl?.classList.add('pointer-events-none');
    syncCursor();
  }

  function movePan(clientX, clientY) {
    if (!isPanning.value) return;
    pendingMoveX = clientX;
    pendingMoveY = clientY;
    hasPendingMove = true;
    if (moveRafId) return;
    moveRafId = requestAnimationFrame(() => {
      moveRafId = 0;
      if (!hasPendingMove || !isPanning.value) return;
      hasPendingMove = false;
      pan.x = panOrigin.x + (pendingMoveX - panStart.x);
      pan.y = panOrigin.y + (pendingMoveY - panStart.y);
      wheelTargetX = pan.x;
      wheelTargetY = pan.y;
      flushTransform();
    });
  }

  function endPan() {
    if (moveRafId) {
      cancelAnimationFrame(moveRafId);
      moveRafId = 0;
    }
    if (hasPendingMove && isPanning.value) {
      hasPendingMove = false;
      pan.x = panOrigin.x + (pendingMoveX - panStart.x);
      pan.y = panOrigin.y + (pendingMoveY - panStart.y);
      wheelTargetX = pan.x;
      wheelTargetY = pan.y;
      flushTransform();
    }
    isPanning.value = false;
    worldEl?.classList.remove('pointer-events-none');
    syncCursor();
  }

  function setSpaceHeld(held) {
    spaceHeld.value = !!held;
    syncCursor();
  }

  function fitToBounds(bounds, containerWidth, containerHeight, padding = 48) {
    if (!bounds || !containerWidth || !containerHeight) {
      resetView();
      return;
    }
    stopWheelEasing();
    const contentWidth = Math.max(bounds.width, 1);
    const contentHeight = Math.max(bounds.height, 1);
    const availW = containerWidth - padding * 2;
    const availH = containerHeight - padding * 2;
    const nextZoom = clampZoom(
      Math.min(availW / contentWidth, availH / contentHeight, 1)
    );
    setZoomValue(nextZoom);
    pan.x =
      padding + (availW - contentWidth * nextZoom) / 2 - bounds.x * nextZoom;
    pan.y =
      padding + (availH - contentHeight * nextZoom) / 2 - bounds.y * nextZoom;
    wheelTargetX = pan.x;
    wheelTargetY = pan.y;
    flushTransform();
    flushZoomLabel();
  }

  function focusRect(rect, containerWidth, containerHeight) {
    if (!rect || !containerWidth || !containerHeight) return;
    stopWheelEasing();
    const pad = 40;
    const scaledW = rect.width * zoom.value;
    const scaledH = rect.height * zoom.value;

    // Prefer centering, but when the page is wider/taller than the viewport
    // pin the top-left so content isn't clipped under the side panels.
    let nextX;
    let nextY;
    if (scaledW <= containerWidth - pad * 2) {
      nextX = containerWidth / 2 - (rect.x + rect.width / 2) * zoom.value;
    } else {
      nextX = pad - rect.x * zoom.value;
    }
    if (scaledH <= containerHeight - pad * 2) {
      nextY = containerHeight / 2 - (rect.y + rect.height / 2) * zoom.value;
    } else {
      nextY = pad - rect.y * zoom.value;
    }

    pan.x = nextX;
    pan.y = nextY;
    wheelTargetX = pan.x;
    wheelTargetY = pan.y;
    flushTransform();
  }

  function dispose() {
    if (rafId) {
      cancelAnimationFrame(rafId);
      rafId = 0;
    }
    if (moveRafId) {
      cancelAnimationFrame(moveRafId);
      moveRafId = 0;
    }
    if (zoomUiRafId) {
      cancelAnimationFrame(zoomUiRafId);
      zoomUiRafId = 0;
    }
    stopWheelEasing();
    worldEl = null;
    viewportEl = null;
    zoomLabelEl = null;
  }

  return {
    pan,
    zoom,
    zoomPercent,
    isPanning,
    spaceHeld,
    bindWorld,
    bindViewport,
    bindZoomLabel,
    dispose,
    zoomTo,
    zoomIn,
    zoomOut,
    zoomAt,
    resetView,
    panBy,
    panByRaf,
    wheelPanBy,
    startPan,
    movePan,
    endPan,
    setSpaceHeld,
    fitToBounds,
    focusRect,
    MIN_ZOOM,
    MAX_ZOOM,
  };
}
